import {
    LayoutDashboard,
    AlertTriangle,
    ExternalLink,
    Table,
    Zap,
    Calendar,
    Mail,
    Bell,
    Database,
    ShieldCheck,
    Wrench,
    FileCode,
    Folder,
    MessageCircle,
    Activity,
    CreditCard,
    TrendingUp,
    PiggyBank,
    FileText,
    MessageCircle as MessageCircleIcon,
    Send as SendIcon,
    X as XIcon,
    RefreshCw,
    Plus,
    Sparkles
} from 'lucide-react';
import React, { useState, useEffect } from 'react';
import DashboardWidgets from '../components/dashboard/DashboardWidgets';
import hqSchema from '../data/hq_schema.json';
import AdminDashboard from '../components/admin/AdminDashboard';
import clientSchema from '../data/client_template_schema.json';
import portalSchema from '../data/portal_schema.json';
import NativeTabTable from '../components/NativeTabTable';
import CalendarView from '../components/scheduling/CalendarView';
import EmailComposer from '../components/communication/EmailComposer';
import ScriptGenerator from '../components/notifications/ScriptGenerator';
import { db } from '../db';
import { useAuth } from '../hooks/useAuth';
// Import view components
import QuotesView from './Billing/QuotesView';
import InvoicesView from './Billing/InvoicesView';
import PaymentsView from './Billing/PaymentsView';
import RefundsView from './Billing/RefundsView';
import CreditView from './Billing/CreditView';
import FilesView from './FilesView';
import CommunicationHistory from './Communication/CommunicationHistory';
import CommunicationComposer from './Communication/CommunicationComposer';

// Data Hub component
const DataHub = ({ onNavigate }) => {
    const dataTabs = [
        { id: 'My_Projects', label: 'Projects', icon: FileText, color: 'bg-blue-50 text-blue-700' },
        { id: 'My_Tasks', label: 'Tasks', icon: Activity, color: 'bg-green-50 text-green-700' },
        { id: 'My_Billing_History', label: 'Billing', icon: PiggyBank, color: 'bg-indigo-50 text-indigo-700' },
        { id: 'My_Files', label: 'Files', icon: Folder, color: 'bg-orange-50 text-orange-700' },
        { id: 'Chat', label: 'Communications', icon: MessageCircle, color: 'bg-purple-50 text-purple-700' }
    ];

    return (
        <div className="bg-white border border-slate-100 rounded-[2.5rem] shadow-sm overflow-hidden">
            <div className="bg-slate-50 px-6 py-4">
                <h2 className="text-xl font-black text-slate-800 tracking-tight">Data Hub</h2>
                <p className="text-slate-400 font-medium text-sm">Business Data Tables</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {dataTabs.map((tab, index) => (
                    <div
                        key={index}
                        className="bg-slate-50 border border-slate-200 rounded-2xl p-4 cursor-pointer hover:bg-slate-50/50 transition-colors"
                        onClick={() => onNavigate(tab.id)}
                    >
                        <div className="flex items-center gap-3 mb-4">
                            <div className={`p-2 rounded-lg ${tab.color}`}>
                                <tab.icon size={18} />
                            </div>
                            <h3 className="font-bold text-slate-700">{tab.label}</h3>
                        </div>
                        <p className="text-slate-500 text-sm">View your {tab.label.toLowerCase()} data</p>
                    </div>
                ))}
            </div>
        </div>
    );
};

// Billing Hub component
const BillingHub = ({ onNavigate }) => {
    const billingItems = [
        { id: 'view:quotes', label: 'Quotes', icon: FileText, color: 'bg-blue-50 text-blue-700' },
        { id: 'view:invoices', label: 'Invoices', icon: CreditCard, color: 'bg-green-50 text-green-700' },
        { id: 'view:payments', label: 'Payments', icon: Activity, color: 'bg-indigo-50 text-indigo-700' },
        { id: 'view:refunds', label: 'Refunds', icon: TrendingUp, color: 'bg-orange-50 text-orange-700' },
        { id: 'view:credit', label: 'Credit', icon: PiggyBank, color: 'bg-purple-50 text-purple-700' }
    ];

    return (
        <div className="bg-white border border-slate-100 rounded-[2.5rem] shadow-sm overflow-hidden">
            <div className="bg-slate-50 px-6 py-4">
                <h2 className="text-xl font-black text-slate-800 tracking-tight">Billing Hub</h2>
                <p className="text-slate-400 font-medium text-sm">Manage your billing and payments</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {billingItems.map((item, index) => (
                    <div
                        key={index}
                        className="bg-slate-50 border border-slate-200 rounded-2xl p-4 cursor-pointer hover:bg-slate-50/50 transition-colors"
                        onClick={() => onNavigate(item.id)}
                    >
                        <div className="flex items-center gap-3 mb-4">
                            <div className={`p-2 rounded-lg ${item.color}`}>
                                <item.icon size={18} />
                            </div>
                            <h3 className="font-bold text-slate-700">{item.label}</h3>
                        </div>
                        <p className="text-slate-500 text-sm">Manage your {item.label.toLowerCase()}</p>
                    </div>
                ))}
            </div>
        </div>
    );
};

// Communications Hub component
const CommunicationsHub = ({ onNavigate, onOpenComposer }) => {
    const [localComposerOpen, setLocalComposerOpen] = useState(false);
    const handleOpen = onOpenComposer || (() => setLocalComposerOpen(true));
    const isOpen = localComposerOpen;

    return (
        <>
            <div className="bg-white border border-slate-100 rounded-[2.5rem] shadow-sm overflow-hidden">
                <div className="bg-slate-50 px-6 py-4">
                    <h2 className="text-xl font-black text-slate-800 tracking-tight">Communications Hub</h2>
                    <p className="text-slate-400 font-medium text-sm">View communication history and send messages</p>
                </div>
                <div className="px-6 py-4">
                    <CommunicationHistory />
                    <div className="mt-6">
                        <button
                            onClick={handleOpen}
                            className="w-full flex items-center justify-between p-3 rounded-xl bg-white border border-slate-100 hover:border-blue-300 hover:shadow-sm text-left group transition-all"
                        >
                            <span className="text-sm font-medium text-slate-600 group-hover:text-slate-900">Send Message</span>
                            <div className="bg-slate-100 px-2 py-0.5 rounded text-[9px] font-bold text-slate-400 group-hover:bg-blue-600 group-hover:text-white uppercase">Send</div>
                        </button>
                    </div>
                </div>
            </div>
            {isOpen && <CommunicationComposer isOpen={isOpen} onClose={() => setLocalComposerOpen(false)} />}
        </>
    );
};

// Files Hub component (fully wired)
const FilesHub = ({ onNavigate }) => {
    return (
        <div className="bg-white border border-slate-100 rounded-[2.5rem] shadow-sm overflow-hidden">
            <div className="bg-slate-50 px-6 py-4">
                <h2 className="text-xl font-black text-slate-800 tracking-tight">Files Hub</h2>
                <p className="text-slate-400 font-medium text-sm">Access your client files via GitHub</p>
            </div>
            <div className="bg-slate-50 px-6 py-4">
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 cursor-pointer hover:bg-slate-50/50 transition-colors"
                    onClick={() => onNavigate('view:files')}
                >
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 rounded-lg bg-blue-50 text-blue-700">
                            <Folder size={18} />
                        </div>
                        <h3 className="font-bold text-slate-700">Client Files</h3>
                    </div>
                    <p className="text-slate-500 text-sm">View and manage your files</p>
                </div>
            </div>
        </div>
    );
};

// Projects and Tasks Hub component
const ProjectsAndTasksHub = ({ onNavigate }) => {
    return (
        <div className="bg-white border border-slate-100 rounded-[2.5rem] shadow-sm overflow-hidden">
            <div className="bg-slate-50 px-6 py-4">
                <h2 className="text-xl font-black text-slate-800 tracking-tight">Projects & Tasks Hub</h2>
                <p className="text-slate-400 font-medium text-sm">Track your projects and tasks</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                    key="projects"
                    className="bg-slate-50 border border-slate-200 rounded-2xl p-4 cursor-pointer hover:bg-slate-50/50 transition-colors"
                    onClick={() => onNavigate('My_Projects')}
                >
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 rounded-lg bg-blue-50 text-blue-700">
                            <FileText size={18} />
                        </div>
                        <h3 className="font-bold text-slate-700">Projects</h3>
                    </div>
                    <p className="text-slate-500 text-sm">View and manage your projects</p>
                </div>
                <div
                    key="tasks"
                    className="bg-slate-50 border border-slate-200 rounded-2xl p-4 cursor-pointer hover:bg-slate-50/50 transition-colors"
                    onClick={() => onNavigate('My_Tasks')}
                >
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 rounded-lg bg-green-50 text-green-700">
                            <Activity size={18} />
                        </div>
                        <h3 className="font-bold text-slate-700">Tasks</h3>
                    </div>
                    <p className="text-slate-500 text-sm">View and manage your tasks</p>
                </div>
            </div>
        </div>
    );
};

const PortalView = ({ role: propRole, userMetadata: propMeta, sheetUrl: propSheetUrl }) => {
    const [selectedTab, setSelectedTab] = useState(null);
    const [userMetadata, setUserMetadata] = useState({
        role: propRole || 'CLIENT',
        sheetUrl: propSheetUrl || propMeta?.sheetUrl || ''
    });
    const [isEmailOpen, setIsEmailOpen] = useState(false);
    const [isComposerOpen, setIsComposerOpen] = useState(false); // For communication composer

    const { user } = useAuth();

    useEffect(() => {
        const loadUserMeta = async () => {
            if (propRole || propSheetUrl || propMeta) {
                setUserMetadata({
                    role: propRole || propMeta?.role || 'CLIENT',
                    sheetUrl: propSheetUrl || propMeta?.sheetUrl || localStorage.getItem('SHEET_URL') || '',
                    ...propMeta
                });
                return;
            }

            let u = null;
            try {
                u = await db.user.toCollection().first();
            } catch (error) {
                console.error("Error loading user from Dexie DB:", error);
            }

            if (u) {
                setUserMetadata({
                    role: u.role || 'CLIENT',
                    sheetUrl: u.sheetUrl || localStorage.getItem('SHEET_URL') || '',
                    ...u
                });
            }
        };
        loadUserMeta();
    }, [propRole, propSheetUrl, propMeta]);

    const role = userMetadata.role;
    const ADMIN_HUB_URL = "https://docs.google.com/spreadsheets/d/11_zPtJPzBmKyKNUK_UMTmnWCFKtaDdcJT19fZFST-FQ";
    const SHEET_URL = role === 'ADMIN' ? ADMIN_HUB_URL : (userMetadata.sheetUrl || "");

    let schema;
    if (role === 'ADMIN') {
        schema = hqSchema;
    } else if (role === 'CLIENT' || role === 'COACH') {
        schema = clientSchema;
    } else {
        schema = portalSchema;
    }

    // Modified getGroupedLinks to adjust virtualLinks for client role
    const getGroupedLinks = () => {
        const baseLinks = Object.entries(schema.tabs)
            .filter(([id, tab]) => {
                const tabName = tab.name || id;

                if (role === 'ADMIN') {
                    return true;
                }

                if (role === 'CLIENT' || role === 'COACH') {
                    const hidden = [
                        'Local Validation', 'Core DB', 'Local_Validation', 'Core_DB',
                        'Coach Chat', 'Coach_Chat', 'Settings', 'Service Training Email',
                        'Service_Training_Email', 'Service Training', 'Service_Training',
                        'Service Training GBP', 'Service_Training_GBP',
                        'My Business Data', 'My_Business_Data'
                    ];
                    if (hidden.some(h => id === h || tabName === h)) {return false;}
                }

                if (role === 'CUSTOMER') {
                    const allowed = ['My_Projects', 'My_Tasks', 'My_Billing_History', 'My_Files', 'Chat'];
                    return allowed.includes(id) || allowed.includes(tabName);
                }

                if (id.endsWith('_Data') && !id.startsWith('Admin_')) {return false;}
                if (id.startsWith('Form_Resp_') || id === 'Daily_Focus_Log') {return false;}

                if (role === 'CLIENT') {
                    const coachTabs = ['Coach_Dashboard', 'Coach_Collateral', 'My_Commissions', 'My_Service_Revenue'];
                    if (coachTabs.includes(id) || coachTabs.includes(tabName)) {return false;}
                }

                return true;
            })
            .map(([id, tab]) => {
                const tabNameParam = tab.name || id;
                const gidParam = tab.gid ? `gid=${tab.gid}` : `range=${tabNameParam}!A1`;
                let cleanSheetUrl = SHEET_URL;
                if (cleanSheetUrl && !cleanSheetUrl.includes('/edit')) {
                    cleanSheetUrl = cleanSheetUrl.replace(/\/ $/, '') + '/edit';
                }
                const finalUrl = cleanSheetUrl ? `${cleanSheetUrl}${cleanSheetUrl.includes('#') || cleanSheetUrl.includes('?') ? (cleanSheetUrl.includes('#') ? '&' : '#') : '#'}${gidParam}` : "";

                return {
                    id,
                    label: tabNameParam.replace(/_/g, ' '),
                    url: finalUrl,
                    icon: id.startsWith('Admin_') ? FileCode : (tab.category === 'core_db' ? Database : Table),
                    color: id.startsWith('Admin_') ? 'bg-emerald-100 text-emerald-700' :
                        tab.category === 'core_db' ? 'bg-indigo-100 text-indigo-700' :
                            'bg-blue-100 text-blue-700',
                    isNative: true,
                    category: tab.category
                };
            });

        // Virtual links - adjust Hub Calendar to be only for ADMIN and COACH
        const virtualLinks = [
            {
                id: 'calendar',
                label: 'Hub Calendar',
                icon: Calendar,
                color: 'bg-amber-100 text-amber-700',
                isNative: true,
                isVirtual: true,
                roles: ['ADMIN', 'COACH']
            },
            {
                id: 'script_generator',
                label: 'Script Generator',
                icon: Sparkles,
                color: 'bg-purple-100 text-purple-700',
                isNative: true,
                isVirtual: true,
                roles: ['ADMIN', 'COACH']
            }
        ];

        const allLinksLocal = [...baseLinks, ...virtualLinks.filter(vl => !vl.roles || vl.roles.includes(role))];

        // Admin groupings
        const adminData = allLinksLocal.filter(l => l.id.startsWith('Admin_'));
        const adminOS = allLinksLocal.filter(l =>
            role === 'ADMIN' &&
            !l.id.startsWith('Admin_') &&
            !l.isVirtual
        );
        const standardTools = allLinksLocal.filter(l => l.isVirtual || (role !== 'ADMIN' && !l.isVirtual));

        return {
            allLinks: allLinksLocal,
            adminDataLinks: adminData,
            adminOSLinks: adminOS,
            standardToolLinks: standardTools
        };
    };

    const {
        allLinks,
        adminDataLinks,
        adminOSLinks,
        standardToolLinks
    } = getGroupedLinks();

    if (selectedTab) {
        // Handle virtual links
        if (selectedTab === 'calendar') {
            return (
                <div className="min-h-screen bg-slate-50">
                    <button onClick={() => setSelectedTab(null)} className="p-6 text-xs font-black uppercase tracking-widest text-slate-400 hover:text-slate-800 flex items-center gap-2 transition-colors">
                        ← Back to Hub
                    </button>
                    <CalendarView userMetadata={userMetadata} />
                </div>
            );
        }

        if (selectedTab === 'script_generator') {
            return (
                <div className="min-h-screen bg-slate-50">
                    <button onClick={() => setSelectedTab(null)} className="p-6 text-xs font-black uppercase tracking-widest text-slate-400 hover:text-slate-800 flex items-center gap-2 transition-colors">
                        ← Back to Hub
                    </button>
                    <div className="max-w-4xl mx-auto px-6">
                        <ScriptGenerator role={role} />
                    </div>
                </div>
            );
        }

        // Handle view IDs (prefixed with 'view:')
        if (selectedTab.startsWith('view:')) {
            const viewId = selectedTab.substring(5); // Remove 'view:' prefix
            return (
                <div className="min-h-screen bg-slate-50">
                    <button onClick={() => setSelectedTab(null)} className="p-6 text-xs font-black uppercase tracking-widest text-slate-400 hover:text-slate-800 flex items-center gap-2 transition-colors">
                        ← Back to Hub
                    </button>
                    <div className="max-w-4xl mx-auto px-6">
                        {viewId === 'quotes' && <QuotesView userEmail={userMetadata?.email} />}
                        {viewId === 'invoices' && <InvoicesView userEmail={userMetadata?.email} />}
                        {viewId === 'payments' && <PaymentsView userEmail={userMetadata?.email} />}
                        {viewId === 'refunds' && <RefundsView userEmail={userMetadata?.email} />}
                        {viewId === 'credit' && <CreditView userEmail={userMetadata?.email} />}
                        {viewId === 'files' && <FilesView userEmail={userMetadata?.email} />}
                        {/* Default fallback */}
                        {!['quotes', 'invoices', 'payments', 'refunds', 'credit', 'files'].includes(viewId) && <div>View not found: {viewId}</div>}
                    </div>
                </div>
            );
        }

        // Handle regular tabs
        const link = allLinks.find(l => l.id === selectedTab);
        return <NativeTabTable
            tabName={selectedTab}
            sheetUrl={link?.url}
            targetClientEmail={propMeta?.email || userMetadata?.email}
            onBack={() => setSelectedTab(null)}
            onNavigate={(newTab) => setSelectedTab(newTab)}
        />;
    }

    const renderLinkGrid = (items, title, icon) => {
        if (items.length === 0) {return null;}
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-3 px-2">
                    {icon && <div className="p-2 bg-slate-100 rounded-lg text-slate-500">{icon}</div>}
                    <h2 className="text-xl font-black text-slate-800 uppercase tracking-tight">{title}</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {items.map((link) => (
                        <div
                            key={link.id}
                            onClick={() => {
                                if (link.id === 'email') {setIsEmailOpen(true);}
                                else {setSelectedTab(link.id);}
                            }}
                            className="group cursor-pointer relative overflow-hidden bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm hover:shadow-xl hover:shadow-blue-900/5 hover:-translate-y-1 transition-all duration-300"
                        >
                            <div className="flex items-start justify-between mb-8">
                                <div className={`p-4 rounded-2xl ${link.color} shadow-inner group-hover:scale-110 transition-transform`}>
                                    <link.icon size={28} />
                                </div>
                                <div className="flex gap-2">
                                    <div
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (link.url && link.url.startsWith('http')) {window.open(link.url, '_blank');}
                                            else {alert("Sheet URL not found. Please sync your account.");}
                                        }}
                                        className="bg-slate-50 p-2 rounded-xl hover:bg-emerald-600 hover:text-white transition-colors cursor-pointer"
                                        title="Open in Google Sheets"
                                    >
                                        <ExternalLink size={18} />
                                    </div>
                                    {!link.isVirtual && (
                                        <div className="bg-slate-50 p-2 rounded-xl group-hover:bg-blue-600 group-hover:text-white transition-colors" title="Open Native App View">
                                            <Table size={18} />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <h3 className="text-xl font-black text-slate-800 tracking-tight group-hover:text-blue-700 transition-colors uppercase mb-2">
                                {link.label}
                            </h3>
                            <div className="flex items-center justify-between">
                                <p className="text-slate-400 text-sm font-medium">
                                    {link.isVirtual ? 'Run Virtual Module' : 'Open Native View'}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Context Notice (Minimized for Admin) */}
            <div className={`bg-gradient-to-br from-blue-900 to-indigo-900 rounded-[2.5rem] p-8 flex flex-col md:flex-row gap-6 items-center text-white relative overflow-hidden group shadow-2xl`}>
                <div className="bg-white/10 p-4 rounded-2xl text-white backdrop-blur-md">
                    <ShieldCheck size={32} />
                </div>
                <div className="flex-1 text-center md:text-left">
                    <h2 className="text-2xl font-black tracking-tight mb-1">Secure Workspace Environment</h2>
                    <p className="text-blue-100/70 font-medium text-sm leading-relaxed max-w-xl">
                        Your data is encrypted and synced with the Chrysalinks Google Ecosystem.
                        Changes made here reflect in your Master Sheets in real-time.
                    </p>
                </div>
                <div className="hidden lg:block">
                    <img src="/logo.png" className="w-16 h-16 opacity-20 grayscale" alt="" />
                </div>
            </div>

            {/* Admin Dashboard Metric Overview */}
            {role === 'ADMIN' ? (
                <AdminDashboard
                    adminDataLinks={adminDataLinks}
                    adminOSLinks={adminOSLinks}
                    standardToolLinks={standardToolLinks}
                    onNavigate={(id) => setSelectedTab(id)}
                />
            ) : (
                <>
                    {/* Hubs for Client View */}
                    <DataHub onNavigate={setSelectedTab} />
                    <BillingHub onNavigate={setSelectedTab} />
                    <CommunicationsHub onNavigate={setSelectedTab} onOpenComposer={() => setIsComposerOpen(true)} />
                    <FilesHub onNavigate={setSelectedTab} />
                    <ProjectsAndTasksHub onNavigate={setSelectedTab} />

                    <DashboardWidgets role={role} userMetadata={userMetadata} />
                    {renderLinkGrid(allLinks, "Business Portal", <LayoutDashboard size={20} />)}
                </>
            )}

            {/* Modals & Overlays */}
            <EmailComposer isOpen={isEmailOpen} onClose={() => setIsEmailOpen(false)} />
            <CommunicationComposer isOpen={isComposerOpen} onClose={() => setIsComposerOpen(false)} />

            <div className="py-12 text-center">
                <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent mb-8 w-1/2 mx-auto" />
                <p className="text-[10px] text-slate-400 font-black uppercase tracking-[0.2em]">
                    Enterprise Security Layer • Chrysalinks Ecosystem
                </p>
            </div>
        </div>
    );
};

export default PortalView;